import styled from "@emotion/styled";
import type { CSSProperties, PropsWithChildren, ReactNode } from "react";
import { Children, useCallback, useState } from "react";

import {
  DragDropProvider,
  RestrictToHorizontalAxis,
  RestrictToVerticalAxis,
  isSortable,
  type DragEndEvent,
  type DragStartEvent,
  type Modifiers,
  type UniqueIdentifier,
} from "@skilitics/drag-drop";

import { dndSensors } from "./dndSensorOptions";
import type { OrderableItemProps } from "./OrderableItem";
import { OrderableItem } from "./OrderableItem";

export const OrderableListContainer = styled.div<{ $sortingStrategy: Props["sortingStrategy"] }>`
  position: relative;
  display: flex;

  ${({ $sortingStrategy }) => {
    if ($sortingStrategy === "horizontal") {
      return `flex-flow: row;`;
    }
    if ($sortingStrategy === "vertical") {
      return `flex-flow: column;`;
    }
    if ($sortingStrategy === "rectangle") {
      return `
        flex-flow: row wrap;
      `;
    }
  }}
`;

type Strategy = "vertical" | "horizontal" | "rectangle";

const modifierByStrategy: Record<Strategy, Modifiers | undefined> = {
  horizontal: [RestrictToHorizontalAxis],
  rectangle: undefined,
  vertical: [RestrictToVerticalAxis],
};

type Props = Pick<OrderableItemProps, "dragSize" | "handle"> & {
  sortingStrategy?: Strategy;
  orderByKey: UniqueIdentifier[];
  onOrderChange?: (key: UniqueIdentifier, newIndex: number) => void;
  onOrdered?: () => void;
  children: ReactNode;
  style?: CSSProperties;
};

export const OrderableList = ({
  children,
  dragSize,
  handle = false,
  sortingStrategy = "vertical",
  onOrderChange,
  onOrdered,
  orderByKey,
  style,
}: PropsWithChildren<Props>) => {
  const [activeIndex, setActiveIndex] = useState<number>();

  const handleDragStart = useCallback(
    ({ operation: { source } }: DragStartEvent) => {
      if (source) {
        setActiveIndex(orderByKey.findIndex((item) => item === source.id));
      }
    },
    [orderByKey],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!event.canceled) {
        const { source } = event.operation;
        if (source && isSortable(source) && source.index !== source.initialIndex) {
          onOrderChange?.(source.id, source.index);
        }
      }

      setActiveIndex(undefined);
      onOrdered?.();
    },
    [onOrderChange, onOrdered],
  );

  const modifiers = modifierByStrategy[sortingStrategy];

  return (
    <DragDropProvider
      sensors={dndSensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={modifiers}
    >
      <OrderableListContainer $sortingStrategy={sortingStrategy} style={style}>
        {orderByKey.map((key, index) => (
          <OrderableItem
            key={key}
            id={key}
            index={index}
            dragSize={dragSize}
            handle={handle}
            dragActive={activeIndex !== undefined}
          >
            {Children.toArray(children)[index]}
          </OrderableItem>
        ))}
      </OrderableListContainer>
    </DragDropProvider>
  );
};
